import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminService from '../services/adminService';
import '../css/admin/OrderManagement.css';
import { FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight, FaSearch, FaFilter, FaClipboardList, FaStore, FaUser, FaMotorcycle, FaDollarSign, FaCreditCard, FaTruck, FaCalendar, FaEye, FaInfoCircle, FaBuilding, FaBox, FaMapMarkerAlt, FaFileExcel, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import * as XLSX from 'xlsx';

// Helper function to check if order was created manually
const isManualOrder = (ord = {}) => {
  if (!ord) return false;
  if (
    ord.is_manual ||
    ord.is_manually_created ||
    ord.created_manually ||
    ord.is_manual_order ||
    ord.is_unrecorded ||
    ord.unrecorded
  ) {
    return true;
  }
  const orderSystem = String(ord.order_system || '').toLowerCase();
  const orderSource = String(ord.order_source || ord.source || '').toLowerCase();
  const channel = String(ord.channel || ord.creation_mode || ord.creation_source || '').toLowerCase();
  const recordStatus = String(ord.record_status || '').toLowerCase();

  if (
    orderSystem === 'unrecorded' ||
    orderSystem === 'manual' ||
    orderSource === 'manual' ||
    orderSource === 'unrecorded' ||
    orderSource === 'data_entry' ||
    orderSource === 'admin' ||
    channel === 'manual' ||
    channel === 'unrecorded' ||
    channel === 'admin' ||
    recordStatus === 'unrecorded' ||
    recordStatus === 'unrecorded_order'
  ) {
    return true;
  }

  const orderId = String(ord.order_number || ord.order_id || ord.id || '');
  if (orderId.startsWith('UNREC_') || orderId.startsWith('MANUAL_') || orderId.startsWith('#UNREC_') || orderId.startsWith('#MANUAL_')) {
    return true;
  }

  if (ord.created_by_name || ord.staff_name || ord.admin_name || ord.creator_name || (ord.created_by && typeof ord.created_by === 'object')) {
    return true;
  }

  return false;
};

const OrderHistory = () => {
  // Responsive state management
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  // Update responsive state on window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      setIsDesktop(width > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStatusDisplay = (order) => {
    if (!order || !order.status) return 'UNKNOWN';
    if ((order.status === 'delivered' || order.status === 'completed') &&
      order.order_type?.toLowerCase() === 'pickup') {
      return 'COMPLETED';
    }
    return order.status.toUpperCase();
  };

  // Helper function to format order ID (e.g. UNREC_53F815 from UNREC_20261003094250_53F815)
  const formatOrderId = (id) => {
    if (!id && id !== 0) return '';
    const str = String(id).trim();
    const cleanStr = str.startsWith('#') ? str.slice(1) : str;
    if (cleanStr.startsWith('UNREC_')) {
      const parts = cleanStr.split('_');
      if (parts.length >= 3) {
        const formatted = `UNREC_${parts[parts.length - 1]}`;
        return str.startsWith('#') ? `#${formatted}` : formatted;
      }
    }
    return str;
  };

  const normalizeBusinessData = (sourceOrder = {}, fallbackOrder = {}) => {
    const rawBusiness = sourceOrder.business || fallbackOrder.business || sourceOrder.business_details || fallbackOrder.business_details || {};
    const business = typeof rawBusiness === 'object' ? rawBusiness : {};
    const businessName = sourceOrder.business_name || fallbackOrder.business_name || business.business_name || business.name || null;
    const businessPhone = sourceOrder.business_phone || sourceOrder.contact_mobile || sourceOrder.business_contact?.business_number || business.business_phone || business.contact_mobile || business.phone || business.businessNumber || null;
    const businessAddress = sourceOrder.business_address || business.business_address || business.address || sourceOrder.address || null;

    const normalizedBusinessDetails = {
      ...business,
      business_id: sourceOrder.business_id || fallbackOrder.business_id || business.business_id || null,
      business_name: businessName,
      business_number: businessPhone,
      business_phone: businessPhone,
      phone: businessPhone,
      address: businessAddress,
      business_address: businessAddress,
      city: business.city || sourceOrder.city || null,
      state: business.state || sourceOrder.state || null,
      pincode: business.pincode || sourceOrder.pincode || null,
      landmark: business.landmark || sourceOrder.landmark || null,
      location: business.location || sourceOrder.location || null
    };

    return {
      business: business,
      business_name: businessName,
      business_id: sourceOrder.business_id || fallbackOrder.business_id || business.business_id || null,
      business_phone: businessPhone,
      business_details: normalizedBusinessDetails,
      business_contact: {
        business_number: businessPhone
      }
    };
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const savedPage = typeof window !== 'undefined' ?
    parseInt(localStorage.getItem('orderHistory_currentPage')) || 1 : 1;

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = n => String(n).padStart(2, '0');
  const toDateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const fromDateStr = `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`;

  const PAGE_SIZE = 10;

  const [filters, setFilters] = useState({
    status_filter: '', // 'completed' | 'delivered' | 'cancelled' | '' = all
    order_type: '',
    business_id: '',
    search: '',
    date_from: fromDateStr,
    date_to: toDateStr,
    payment_status: '',
    source: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [dateFromInput, setDateFromInput] = useState(fromDateStr);
  const [dateToInput, setDateToInput] = useState(toDateStr);

  // All fetched + filtered orders live here; we paginate them client-side
  const [allOrders, setAllOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(savedPage);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('orderHistory_activeTab') || 'online');
  const [kpiMetrics, setKpiMetrics] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [serverPagination, setServerPagination] = useState(null);

  // Derived client-side 10 per page pagination
  const totalOrders = allOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalOrders / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const orders = allOrders;
  const displayedOrders = allOrders.slice(startIndex, startIndex + PAGE_SIZE);
  const pagination = {
    total_orders: totalOrders,
    current_page: safePage,
    total_pages: totalPages,
    per_page: PAGE_SIZE,
  };

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [orderTypeDropdownOpen, setOrderTypeDropdownOpen] = useState(false);
  const [paymentStatusDropdownOpen, setPaymentStatusDropdownOpen] = useState(false);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const modalOpenTimeRef = useRef(0);

  useEffect(() => {
    if (showDetailsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showDetailsModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen && !event.target.closest('.history-status-dropdown')) {
        setStatusDropdownOpen(false);
      }
      if (orderTypeDropdownOpen && !event.target.closest('.order-type-dropdown')) {
        setOrderTypeDropdownOpen(false);
      }
      if (paymentStatusDropdownOpen && !event.target.closest('.payment-status-dropdown')) {
        setPaymentStatusDropdownOpen(false);
      }
      if (sourceDropdownOpen && !event.target.closest('.source-dropdown')) {
        setSourceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownOpen, orderTypeDropdownOpen, paymentStatusDropdownOpen]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchOrders = useCallback(async (targetPage = currentPage, silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const requestParams = {
        page: 1,
        limit: 200,
        date_from: filters.date_from,
        date_to: filters.date_to,
        ...(filters.business_id && { business_id: filters.business_id }),
      };

      let firstRes;
      if (activeTab === 'counter') {
        firstRes = await AdminService.getCounterOrders({ ...requestParams, status_group: 'history' });
      } else {
        firstRes = await AdminService.getOrderHistory(requestParams);
      }

      let rawOrders = firstRes.orders || firstRes.results || firstRes.data?.orders || firstRes.data?.results || [];
      if (activeTab === 'counter') {
        rawOrders = rawOrders.map(o => ({ ...o, order_system: 'counter' }));
      }
      let collected = [...rawOrders];

      const backendTotalPages = firstRes.pagination?.total_pages || firstRes.pages || firstRes.data?.pagination?.total_pages || 1;

      if (firstRes.kpi_metrics) {
        setKpiMetrics(firstRes.kpi_metrics);
      } else if (firstRes.data && firstRes.data.kpi_metrics) {
        setKpiMetrics(firstRes.data.kpi_metrics);
      } else {
        setKpiMetrics(null);
      }

      if (backendTotalPages > 1) {
        const maxPagesToFetch = Math.min(backendTotalPages, 2);
        const pageNumbers = Array.from({ length: maxPagesToFetch - 1 }, (_, i) => i + 2);
        const restResults = await Promise.all(
          pageNumbers.map(p => {
            const pageParams = { ...requestParams, page: p };
            if (activeTab === 'counter') {
              return AdminService.getCounterOrders({ ...pageParams, status_group: 'history' });
            } else {
              return AdminService.getOrderHistory(pageParams);
            }
          })
        );
        for (const r of restResults) {
          let pOrders = r.orders || r.results || r.data?.orders || r.data?.results || [];
          if (activeTab === 'counter') {
            pOrders = pOrders.map(o => ({ ...o, order_system: 'counter' }));
          }
          collected = collected.concat(pOrders);
        }
      }

      // De-duplicate orders by order_system + order_id
      const seenKeys = new Set();
      collected = collected.filter(o => {
        const key = `${o.order_system || 'standard'}-${o.order_id || o.id}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      let filteredOrders = collected.filter(order => {
        if (filters.status_filter && (order.status || '').toLowerCase() !== filters.status_filter.toLowerCase()) return false;
        if (filters.order_type) {
          const orderType = (order.order_type || '').toLowerCase().replace(/[-_]/g, ' ').trim();
          const filterType = filters.order_type.toLowerCase().replace(/[-_]/g, ' ').trim();
          if (filterType.includes('porter')) {
            if (!orderType.includes('porter')) return false;
          } else if (orderType !== filterType) return false;
        }
        if (filters.payment_status) {
          const currentPs = (order.payment_status || '').toLowerCase().trim();
          const targetPs = filters.payment_status.toLowerCase().trim();
          if (targetPs === 'paid' || targetPs === 'success') {
            if (currentPs !== 'paid' && currentPs !== 'success') return false;
          } else if (targetPs === 'completed') {
            if (currentPs !== 'completed') return false;
          } else if (targetPs === 'pending' || targetPs === 'unpaid') {
            if (currentPs !== 'pending' && currentPs !== 'unpaid') return false;
          } else if (targetPs === 'cod') {
            if (currentPs !== 'cod' && currentPs !== 'pay_on_delivery' && currentPs !== 'cash_on_delivery') return false;
          } else if (currentPs !== targetPs) return false;
        }
        if (filters.source && filters.source.trim()) {
          const srcFilter = filters.source.trim().toLowerCase();
          const orderSource = String(
            order?.source ||
            order?.order_source ||
            order?.source_type ||
            order?.payment_source ||
            order?.payment_details?.payment_source ||
            order?.channel ||
            ''
          ).trim().toLowerCase();

          if (!orderSource.includes(srcFilter) && orderSource !== srcFilter) return false;
        }
        if (filters.search && filters.search.trim()) {
          const q = filters.search.trim().toLowerCase();
          const orderIdStr = String(order.order_id || order.id || '').toLowerCase();
          const custNameStr = String(order.customer_name || '').toLowerCase();
          const custPhoneStr = String(order.customer_phone || order.mobileNumber || '').toLowerCase();
          const bizNameStr = String(order.business_name || '').toLowerCase();

          if (!orderIdStr.includes(q) && !custNameStr.includes(q) && !custPhoneStr.includes(q) && !bizNameStr.includes(q)) return false;
        }
        return true;
      });

      const totalCount = filteredOrders.length;
      const totalPagesCount = Math.max(1, Math.ceil(totalCount / 10));

      setAllOrders(filteredOrders);
      setServerPagination({
        total_orders: totalCount,
        current_page: Math.min(targetPage, totalPagesCount),
        total_pages: totalPagesCount,
        per_page: 10
      });

    } catch (err) {
      setError('Failed to fetch order history');
      console.error('Order history fetch error:', err);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters.status_filter, filters.order_type, filters.business_id, filters.search, filters.date_from, filters.date_to, filters.payment_status, filters.source, currentPage]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrders(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Available payment sources (web, android, whatsapp_order, etc.) extracted dynamically from response payment_source
  const availableSources = React.useMemo(() => {
    const defaultSources = ['web', 'android', 'whatsapp_order'];
    const seen = new Set(defaultSources);
    (allOrders || []).forEach(o => {
      const src = o?.payment_source || o?.payment_details?.payment_source || o?.source;
      if (src && !seen.has(src)) {
        seen.add(src);
      }
    });
    return Array.from(seen);
  }, [allOrders]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleDateChange = (key, val) => {
    if (key === 'date_from') setDateFromInput(val);
    if (key === 'date_to') setDateToInput(val);

    if (!val) {
      handleFilterChange(key, '');
      return;
    }

    if (/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(val)) {
      const year = parseInt(val.split('-')[0], 10);
      if (year >= 1900 && year <= 2100) {
        handleFilterChange(key, val);
      }
    }
  };

  const handleDateKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      const val = key === 'date_from' ? dateFromInput : dateToInput;
      if (!val || /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(val)) {
        handleFilterChange(key, val);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      if (typeof window !== 'undefined') {
        localStorage.setItem('orderHistory_currentPage', newPage.toString());
      }
    }
  };

  const handleSearchChange = (value) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const newTimeout = setTimeout(() => {
      handleFilterChange('search', value);
    }, 300);
    setSearchTimeout(newTimeout);
  };

  const handleOpenDetailsModal = async (order) => {
    // Show modal immediately with table data so user sees something right away
    const isPorter = order.order_type?.toLowerCase() === 'porter_delivery';
    const itemsSubtotal = (order.items || []).reduce((sum, item) => sum + (Number(item.total_price) || (Number(item.unit_price || item.price || 0) * Number(item.quantity || 1)) || 0), 0);
    let subtotalVal = order.total_amount;
    if ((subtotalVal == null || Number(subtotalVal) === 0) && itemsSubtotal > 0) {
      subtotalVal = itemsSubtotal;
    }
    if ((subtotalVal == null || Number(subtotalVal) === 0) && isPorter) {
      subtotalVal = order.total_price || 0;
    }
    const normalizedFallbackBusiness = normalizeBusinessData(order);
    const isManualFallback = isManualOrder(order);
    const manualCreatorFallback = (order.created_by && typeof order.created_by === 'object') ? order.created_by : (order.ordered_by && typeof order.ordered_by === 'object' ? order.ordered_by : null);
    const fallbackOrderedByName = isManualFallback
      ? (manualCreatorFallback?.name || manualCreatorFallback?.username || order.created_by_name || order.staff_name || order.admin_name || order.creator_name || order.customer_name)
      : (manualCreatorFallback?.name || order.created_by_name || order.staff_name || null);
    const fallbackOrderedByPhone = isManualFallback
      ? (manualCreatorFallback?.phone || manualCreatorFallback?.mobile || order.created_by_phone || order.staff_phone || order.customer_phone || order.phone_number)
      : (manualCreatorFallback?.phone || manualCreatorFallback?.mobile || order.created_by_phone || order.staff_phone || null);

    const fallbackOrder = {
      ...order,
      items: order.items || [],
      customer: order.customer || {
        display_name: order.customer_name,
        phone: order.customer_phone,
        id: order.user_id
      },
      business: normalizedFallbackBusiness.business,
      business_name: normalizedFallbackBusiness.business_name || order.business_name || 'N/A',
      business_id: normalizedFallbackBusiness.business_id || order.business_id || null,
      business_details: normalizedFallbackBusiness.business_details,
      business_contact: normalizedFallbackBusiness.business_contact,
      business_phone: normalizedFallbackBusiness.business_phone || order.business_phone || null,
      ordered_by_name: fallbackOrderedByName,
      ordered_by_phone: fallbackOrderedByPhone,
      delivery_partner_name: order.delivery_partner_name || order.delivery_partner?.name,
      delivery_partner_phone: order.delivery_partner_phone || order.delivery_partner?.phone,
      payment_status: order.payment_status,
      summary: order.summary || {
        subtotal: subtotalVal,
        total: order.final_amount || subtotalVal
      }
    };

    setSelectedOrder(fallbackOrder);
    modalOpenTimeRef.current = Date.now();
    setShowDetailsModal(true);

    // Then try to enrich with full API data in background
    try {
      const response = await AdminService.getOrderDetails(order.order_id, order.order_system || 'standard', order, order);
      const orderData = response.data?.order || (response.data && !response.data.order ? response.data : null) || response.order;
      if (response.success && orderData) {
        // Format delivery address if it's an object
        let formattedDeliveryAddress = '';
        if (orderData.delivery_address) {
          if (typeof orderData.delivery_address === 'object') {
            const addr = orderData.delivery_address;
            const parts = [
              addr.door_no || addr.doorNo || addr.flat_no || addr.flatNo || addr.door,
              addr.street || addr.area || addr.locality,
              addr.city,
              addr.state,
              addr.pincode || addr.pin_code || addr.pin,
              addr.country
            ].filter(Boolean);
            formattedDeliveryAddress = parts.length > 0 ? parts.join(', ') : (addr.address || addr.formatted_address || '');
          } else {
            formattedDeliveryAddress = orderData.delivery_address;
          }
        }

        // Normalise items structure
        let normalizedItems = [];
        if (orderData.items) {
          normalizedItems = orderData.items.map(item => ({
            ...item,
            name: item.name || item.item_name || '',
            total_price: item.total_price || (item.unit_price * item.quantity) || 0,
            image: item.image || item.item_image || null
          }));
        }

        // Normalise summary
        const taxVal = Number(orderData.total_gst_amount ?? orderData.gst_amount ?? orderData.gst_total ?? orderData.tax ?? 0);
        const deliveryVal = Number(orderData.delivery_charges || 0);
        const parcelVal = Number(orderData.parcel_charges || 0);
        const discountVal = Number(orderData.discount_amount ?? orderData.discount ?? 0);
        const totalVal = Number(orderData.final_amount ?? orderData.total_amount ?? 0);
        let subtotalVal = orderData.subtotal != null
          ? Number(orderData.subtotal)
          : (orderData.total_amount != null
            ? Number(orderData.total_amount) - deliveryVal - parcelVal - taxVal + discountVal
            : 0);

        if (subtotalVal <= 0 && normalizedItems.length > 0) {
          subtotalVal = normalizedItems.reduce((sum, it) => sum + (Number(it.total_price) || (Number(it.unit_price || it.price || 0) * Number(it.quantity || 1)) || 0), 0);
        }

        const summary = {
          subtotal: subtotalVal,
          delivery_charges: deliveryVal,
          parcel_charges: parcelVal,
          tax: taxVal,
          discount: discountVal,
          total: totalVal || (subtotalVal + deliveryVal + parcelVal + taxVal - discountVal)
        };

        const normalizedBusiness = normalizeBusinessData(orderData, order);

        // Extract customer name & phone prioritising delivery_address object details (e.g. name, mobile, phone, customer_name)
        const addrObj = (orderData.delivery_address && typeof orderData.delivery_address === 'object') ? orderData.delivery_address : {};
        const deliveryCustName = addrObj.name || addrObj.customer_name || addrObj.receiver_name || addrObj.recipient_name;
        const deliveryCustPhone = addrObj.mobile || addrObj.phone || addrObj.customer_phone || addrObj.receiver_phone || addrObj.contact_number;

        const effectiveCustName = deliveryCustName || orderData.customer_name || (orderData.first_name ? `${orderData.first_name} ${orderData.last_name || ''}`.trim() : order.customer_name);
        const effectiveCustPhone = deliveryCustPhone || orderData.customer_phone || orderData.phone_number || order.customer_phone || '';

        // Capture who originally created/placed the order if created manually
        const isManual = isManualOrder(orderData) || isManualOrder(order);
        const manualCreatorObj = (orderData.created_by && typeof orderData.created_by === 'object') ? orderData.created_by : ((order.created_by && typeof order.created_by === 'object') ? order.created_by : (orderData.ordered_by && typeof orderData.ordered_by === 'object' ? orderData.ordered_by : null));

        const orderedByName = isManual
          ? (manualCreatorObj?.name || manualCreatorObj?.username || orderData.created_by_name || orderData.staff_name || orderData.admin_name || orderData.creator_name || orderData.customer_name || (orderData.first_name ? `${orderData.first_name} ${orderData.last_name || ''}`.trim() : '') || order.customer_name)
          : (manualCreatorObj?.name || orderData.created_by_name || orderData.staff_name || null);

        const orderedByPhone = isManual
          ? (manualCreatorObj?.phone || manualCreatorObj?.mobile || orderData.created_by_phone || orderData.staff_phone || orderData.customer_phone || orderData.phone_number || order.customer_phone)
          : (manualCreatorObj?.phone || manualCreatorObj?.mobile || orderData.created_by_phone || orderData.staff_phone || null);

        setSelectedOrder({
          ...orderData,
          business: orderData.business || normalizedBusiness.business,
          business_name: orderData.business_name || normalizedBusiness.business_name || order.business_name || 'N/A',
          business_id: orderData.business_id || normalizedBusiness.business_id || order.business_id || null,
          business_details: orderData.business_details || normalizedBusiness.business_details,
          business_contact: orderData.business_contact || normalizedBusiness.business_contact,
          business_phone: orderData.business_phone || normalizedBusiness.business_phone || order.business_phone || null,
          delivery_address: formattedDeliveryAddress || order.delivery_address || 'N/A',
          items: normalizedItems,
          summary: orderData.summary || summary,
          customer_name: effectiveCustName,
          customer_phone: effectiveCustPhone,
          ordered_by_name: orderedByName,
          ordered_by_phone: orderedByPhone,
          customer: {
            ...orderData.customer,
            display_name: effectiveCustName,
            name: effectiveCustName,
            phone: effectiveCustPhone,
            id: orderData.user_id || orderData.customer?.id || order.user_id
          },
          delivery_partner_name: orderData.delivery_partner_name || orderData.delivery_partner?.name || order.delivery_partner_name || order.delivery_partner?.name,
          delivery_partner_phone: orderData.delivery_partner_phone || orderData.delivery_partner?.phone || order.delivery_partner_phone || order.delivery_partner?.phone,
          payment_status: order.payment_status || orderData.payment_status
        });
      }
    } catch (err) {
      // fallback already shown, nothing to do
      console.warn('Could not enrich order details:', err);
    }
  };

  const formatDate = (dateString) => AdminService.formatDate(dateString);
  const formatCurrency = (amount) => AdminService.formatCurrency(amount);
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const datePart = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    return `${datePart} ${timePart}`;
  };
  const formatTimelineTimestamp = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return `[${dateString}]`;
    const datePart = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    return `[${datePart}, ${timePart}]`;
  };

  const formatStatusTimelineForExcel = (order) => {
    if (!Array.isArray(order?.status_timeline) || order.status_timeline.length === 0) {
      return 'N/A';
    }
    return order.status_timeline.map(st => {
      const statusLabel = st.label || st.status || '';
      const timeStr = st.timestamp ? formatTimelineTimestamp(st.timestamp) : '';
      const byStr = st.changed_by || st.source ? ` (By: ${st.changed_by || st.source})` : '';
      const notesStr = st.notes ? ` - Notes: ${st.notes}` : '';
      return `${statusLabel}${timeStr ? ' ' + timeStr : ''}${byStr}${notesStr}`;
    }).join('\n');
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const fetchAllPages = async (extraParams = {}) => {
        const params = {
          page: 1, limit: 200,
          date_from: filters.date_from,
          date_to: filters.date_to,
          ...(filters.business_id && { business_id: filters.business_id }),
          ...extraParams,
        };
        let firstRes;
        if (activeTab === 'counter') {
          firstRes = await AdminService.getCounterOrders({ ...params, status_group: 'history' });
        } else {
          firstRes = await AdminService.getOrderHistory(params);
        }
        let collected = firstRes.orders || firstRes.data?.orders || [];
        const tp = firstRes.pagination?.total_pages || 1;
        if (tp > 1) {
          const rest = await Promise.all(
            Array.from({ length: tp - 1 }, (_, i) => i + 2).map(p => {
              if (activeTab === 'counter') {
                return AdminService.getCounterOrders({ ...params, status_group: 'history', page: p });
              } else {
                return AdminService.getOrderHistory({ ...params, page: p });
              }
            })
          );
          for (const r of rest) collected = collected.concat(r.orders || r.data?.orders || []);
        }
        return collected;
      };

      const fetchedOrders = await fetchAllPages();

      // Filter ALL fetched orders strictly by date range and active filters
      const fromDate = filters.date_from ? new Date(filters.date_from + 'T00:00:00') : null;
      const toDate = filters.date_to ? new Date(filters.date_to + 'T23:59:59') : null;

      const rawExportOrders = fetchedOrders.filter(order => {
        if (fromDate || toDate) {
          const d = new Date(order.created_at);
          if (fromDate && d < fromDate) return false;
          if (toDate && d > toDate) return false;
        }
        if (filters.status_filter) {
          const st = (order.status || '').toLowerCase();
          if (st !== filters.status_filter.toLowerCase()) return false;
        }
        if (filters.order_type) {
          const filterType = filters.order_type.toLowerCase().trim().replace(/[-_]/g, ' ');
          const orderType = (order.order_type || '').toLowerCase().trim().replace(/[-_]/g, ' ');
          if (filterType === 'dine in') {
            if (orderType !== 'dine in' && orderType !== 'dine-in' && orderType !== 'dine_in') return false;
          } else if (orderType !== filterType) {
            return false;
          }
        }
        if (filters.payment_status) {
          const ps = (order.payment_status || '').toLowerCase().trim();
          const targetPs = filters.payment_status.toLowerCase().trim();
          if (targetPs === 'paid') {
            if (ps !== 'paid' && ps !== 'success') return false;
          } else if (ps !== targetPs) return false;
        }
        if (filters.source) {
          const src = (order.payment_source || order.payment_details?.payment_source || order.source || '').toLowerCase().trim();
          if (src !== filters.source.toLowerCase().trim()) return false;
        }
        if (filters.search && filters.search.trim()) {
          const q = filters.search.trim().toLowerCase();
          const qNormalized = q.replace(/[-_]/g, ' ');
          const orderIdStr = String(order.order_id || '').toLowerCase();
          const orderNumStr = String(order.order_number || '').toLowerCase();
          const custNameStr = String(order.customer_name || '').toLowerCase();
          const custPhoneStr = String(order.customer_phone || '').toLowerCase();
          const bizNameStr = String(order.business_name || '').toLowerCase();
          const partnerNameStr = String(order.delivery_partner_name || '').toLowerCase();
          const orderTypeStr = String(order.order_type || '').toLowerCase();
          const orderTypeNormalized = orderTypeStr.replace(/[-_]/g, ' ');

          const matches = orderIdStr.includes(q) ||
            orderNumStr.includes(q) ||
            custNameStr.includes(q) ||
            custPhoneStr.includes(q) ||
            bizNameStr.includes(q) ||
            partnerNameStr.includes(q) ||
            orderTypeStr.includes(q) ||
            orderTypeNormalized.includes(qNormalized);

          if (!matches) return false;
        }
        return true;
      });

      rawExportOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      if (rawExportOrders.length === 0) {
        alert('No orders match the selected date filter to export.');
        return;
      }

      // Enrich orders with status_timeline from getOrderDetails if missing
      const exportOrders = [];
      const BATCH_SIZE = 15;
      for (let i = 0; i < rawExportOrders.length; i += BATCH_SIZE) {
        const batch = rawExportOrders.slice(i, i + BATCH_SIZE);
        const batchEnriched = await Promise.all(
          batch.map(async (order) => {
            if (Array.isArray(order.status_timeline) && order.status_timeline.length > 0) {
              return order;
            }
            try {
              const res = await AdminService.getOrderDetails(order.order_id, order.order_system || 'standard');
              const details = res.data?.order || (res.data && !res.data.order ? res.data : null) || res.order || res.data;
              if (details && Array.isArray(details.status_timeline) && details.status_timeline.length > 0) {
                return { ...order, status_timeline: details.status_timeline };
              }
            } catch (e) {
              console.error(`Failed to enrich timeline for order #${order.order_id}:`, e);
            }
            return order;
          })
        );
        exportOrders.push(...batchEnriched);
      }

      const rows = exportOrders.map(order => ({
        'Order #': `#${order.order_id}`,
        'Order Number': order.order_number || '',
        'Customer Name': order.customer_name || '',
        'Customer Phone': order.customer_phone || '',
        'Business': order.business_name || '',
        'Order Type': order.order_type || '',
        'Amount (₹)': order.final_amount ?? order.total_amount ?? '',
        'Order Status': getStatusDisplay(order),
        'Payment Status': (order.payment_status || 'PENDING').toUpperCase(),
        'Delivery Partner': order.delivery_partner_name || 'N/A',
        'Status Timeline': formatStatusTimelineForExcel(order),
        'Date': formatDate(order.created_at),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Order History');

      const colWidths = Object.keys(rows[0]).map(key => ({
        wch: Math.min(
          Math.max(
            key.length,
            ...rows.map(r => {
              const val = String(r[key] ?? '');
              return Math.max(...val.split('\n').map(l => l.length));
            })
          ) + 2,
          65
        )
      }));
      worksheet['!cols'] = colWidths;

      const parts = ['order_history'];
      if (filters.date_from) parts.push(`from-${filters.date_from}`);
      if (filters.date_to) parts.push(`to-${filters.date_to}`);
      XLSX.writeFile(workbook, `${parts.join('_')}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="order-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading order history...</p>
      </div>
    );
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('orderHistory_activeTab', tab);
    setCurrentPage(1);
    setKpiMetrics(null);
    setSearchInput('');
    setFilters(prev => ({
      ...prev,
      status_filter: '',
      order_type: '',
      business_id: '',
      search: '',
      payment_status: '',
      source: ''
    }));
  };

  return (
    <div className={`order-management ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>

      {/* Header */}
      <div className={`order-management-header ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
        <div className="header-content">
          <div className="header-text">
            <h2>Order History</h2>
            <p style={{ margin: 0, fontSize: '13px', color: '#666', marginTop: '2px' }}>
              Completed, Delivered &amp; Cancelled orders
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="header-refresh-btn" onClick={() => fetchOrders()}>
              <MdRefresh className="refresh-icon" />
              Refresh
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportLoading || !orders || orders.length === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px',
                background: exportLoading || !orders || orders.length === 0 ? '#9ca3af' : '#1d6f42',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: exportLoading || !orders || orders.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={e => { if (!exportLoading && orders?.length > 0) e.currentTarget.style.background = '#155534'; }}
              onMouseLeave={e => { if (!exportLoading && orders?.length > 0) e.currentTarget.style.background = '#1d6f42'; }}
            >
              {exportLoading
                ? <><MdRefresh style={{ fontSize: '14px', animation: 'spin 0.8s linear infinite' }} /> Exporting...</>
                : <><FaFileExcel style={{ fontSize: '14px' }} /> Export</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="order-tabs-container">
        <button
          onClick={() => handleTabChange('online')}
          className={`order-tab-btn ${activeTab === 'online' ? 'active' : ''}`}
        >
          Online Orders
        </button>
        <button
          onClick={() => handleTabChange('counter')}
          className={`order-tab-btn ${activeTab === 'counter' ? 'active' : ''}`}
        >
          Counter Orders
        </button>
      </div>

      {/* KPI Metrics */}
      {kpiMetrics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '24px',
          marginTop: '10px'
        }}>
          {Object.entries(kpiMetrics).map(([key, val]) => {
            // Skip objects (like status_counts) or arrays
            if (typeof val === 'object' || Array.isArray(val) || val === null || val === undefined) return null;

            // Format key to a human-readable title (e.g. "total_revenue" -> "Total Revenue")
            const displayTitle = key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());

            // Format value (e.g. prefix with Currency symbol if it represents an amount)
            const isAmount = key.includes('revenue') || key.includes('value') || key.includes('discount') || key.includes('tax') || key.includes('amount') || key.includes('gst');
            const displayVal = isAmount
              ? `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : val;

            // Pick icon and color dynamically based on key
            let icon = <FaClipboardList />;
            let iconBg = '#eff6ff';
            let iconColor = '#3b82f6';

            if (key.includes('revenue')) {
              icon = <FaDollarSign />;
              iconBg = '#ecfdf5';
              iconColor = '#10b981';
            } else if (key.includes('value')) {
              icon = <FaInfoCircle />;
              iconBg = '#fdf4ff';
              iconColor = '#d946ef';
            } else if (key.includes('discount')) {
              icon = <FaExclamationTriangle style={{ transform: 'rotate(180deg)' }} />;
              iconBg = '#fef2f2';
              iconColor = '#ef4444';
            } else if (key.includes('tax') || key.includes('gst')) {
              icon = <FaBuilding />;
              iconBg = '#fffbeb';
              iconColor = '#f59e0b';
            }

            return (
              <div
                key={key}
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div style={{
                  background: iconBg,
                  color: iconColor,
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {displayTitle}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginTop: '4px' }}>
                    {displayVal}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className={`order-filters ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: '12px 16px', padding: '16px 20px', overflow: 'visible', position: 'relative', zIndex: 20 }}>

        {/* Search Orders */}
        <div className={`filter-group ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`} style={{ minWidth: '200px', flex: '1 1 240px' }}>
          <label>Search Orders</label>
          <div className="search-input-container" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
              <FaSearch className="search-icon" style={{ position: 'absolute', left: '10px', color: '#94a3b8', fontSize: '13px' }} />
              <input
                type="text"
                placeholder="Order ID, Customer, Phone, Business..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFilterChange('search', searchInput);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '7px 24px 7px 30px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '13px',
                  outline: 'none',
                  background: '#ffffff'
                }}
              />
              {searchInput && (
                <FaTimes
                  onClick={() => {
                    setSearchInput('');
                    handleFilterChange('search', '');
                  }}
                  style={{ position: 'absolute', right: '8px', color: '#94a3b8', cursor: 'pointer', fontSize: '11px' }}
                  title="Clear Search"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => handleFilterChange('search', searchInput)}
              style={{
                padding: '7px 12px',
                background: '#F55D00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s ease',
                height: '33px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Apply
            </button>
          </div>
        </div>

        {/* Order Status */}
        <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 120px' }}>
          <label>Order Status</label>
          <div className="history-status-dropdown custom-dropdown">
            <div className="dropdown-input" onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}>
              <span className="dropdown-placeholder">
                {filters.status_filter === 'completed' ? 'COMPLETED' :
                  filters.status_filter === 'delivered' ? 'DELIVERED' :
                    filters.status_filter === 'cancelled' ? 'CANCELLED' :
                      'All Statuses'}
              </span>
              <FaChevronDown className={`dropdown-arrow ${statusDropdownOpen ? 'open' : ''}`} />
            </div>
            {statusDropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-option" onClick={() => { handleFilterChange('status_filter', ''); setStatusDropdownOpen(false); }}>
                  All Statuses
                </div>
                {['completed', 'delivered', 'cancelled'].map(s => (
                  <div key={s} className="dropdown-option" onClick={() => {
                    handleFilterChange('status_filter', s);
                    setStatusDropdownOpen(false);
                  }}>
                    {s.toUpperCase()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Order Type */}
        <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 120px' }}>
          <label>Order Type</label>
          <div className="order-type-dropdown custom-dropdown">
            <div className="dropdown-input" onClick={() => setOrderTypeDropdownOpen(!orderTypeDropdownOpen)}>
              <span className="dropdown-placeholder">
                {filters.order_type === 'delivery' ? 'Delivery' :
                  filters.order_type === 'pickup' ? 'Pickup' :
                    filters.order_type === 'dine-in' ? 'Dine In' :
                      filters.order_type === 'takeaway' ? 'Takeaway' :
                        filters.order_type === 'porter_delivery' || filters.order_type === 'porter delivery' ? 'Porter Delivery' :
                          'All Types'}
              </span>
              <FaChevronDown className={`dropdown-arrow ${orderTypeDropdownOpen ? 'open' : ''}`} />
            </div>
            {orderTypeDropdownOpen && (
              <div className="dropdown-menu">
                {[
                  { val: '', label: 'All Types' },
                  { val: 'delivery', label: 'Delivery' },
                  { val: 'pickup', label: 'Pickup' },
                  { val: 'dine-in', label: 'Dine In' },
                  { val: 'takeaway', label: 'Takeaway' },
                  ...(activeTab !== 'counter' ? [{ val: 'porter delivery', label: 'Porter Delivery' }] : [])
                ].map(({ val, label }) => (
                  <div key={val} className="dropdown-option" onClick={() => {
                    handleFilterChange('order_type', val);
                    setOrderTypeDropdownOpen(false);
                  }}>{label}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment Status */}
        <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 120px' }}>
          <label>Payment Status</label>
          <div className="payment-status-dropdown custom-dropdown">
            <div className="dropdown-input" onClick={() => setPaymentStatusDropdownOpen(!paymentStatusDropdownOpen)}>
              <span className="dropdown-placeholder">
                {filters.payment_status ? (
                  filters.payment_status === 'success' ? 'Success' :
                    filters.payment_status === 'pending' ? 'Pending' :
                      filters.payment_status === 'failed' ? 'Failed' :
                        filters.payment_status === 'cancelled' ? 'Cancelled' :
                          filters.payment_status === 'paid' ? 'Paid' :
                            filters.payment_status === 'completed' ? 'Completed' :
                              filters.payment_status.toUpperCase()
                ) : 'All Payments'}
              </span>
              <FaChevronDown className={`dropdown-arrow ${paymentStatusDropdownOpen ? 'open' : ''}`} />
            </div>
            {paymentStatusDropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-option" onClick={() => { handleFilterChange('payment_status', ''); setPaymentStatusDropdownOpen(false); }}>
                  All Payments
                </div>
                {(activeTab === 'counter' ? ['paid', 'pending', 'completed'] : ['success', 'pending', 'cod', 'failed', 'cancelled']).map(ps => (
                  <div key={ps} className="dropdown-option" onClick={() => {
                    handleFilterChange('payment_status', ps);
                    setPaymentStatusDropdownOpen(false);
                  }}>
                    {ps.toUpperCase()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Source */}
        {activeTab !== 'counter' && (
          <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 120px' }}>
            <label>Source</label>
            <div className="source-dropdown custom-dropdown">
              <div
                className="dropdown-input"
                onClick={() => setSourceDropdownOpen(!sourceDropdownOpen)}
              >
                <span className="dropdown-placeholder">
                  {filters.source ? filters.source.replace(/_/g, ' ').toUpperCase() : 'All Sources'}
                </span>
                <FaChevronDown className={`dropdown-arrow ${sourceDropdownOpen ? 'open' : ''}`} />
              </div>

              {sourceDropdownOpen && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-option"
                    onClick={() => {
                      handleFilterChange('source', '');
                      setSourceDropdownOpen(false);
                    }}
                  >
                    All Sources
                  </div>
                  {availableSources.map(src => (
                    <div
                      key={src}
                      className="dropdown-option"
                      onClick={() => {
                        handleFilterChange('source', src);
                        setSourceDropdownOpen(false);
                      }}
                    >
                      {src.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Date Range */}
        <div className="filter-group" style={{ minWidth: '240px', flex: '1 1 240px' }}>
          <label>Date Range</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="date"
              value={dateFromInput || ''}
              onChange={(e) => handleDateChange('date_from', e.target.value)}
              onKeyDown={(e) => handleDateKeyDown(e, 'date_from')}
              style={{ padding: '8px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', width: '50%', outline: 'none', background: '#ffffff' }}
            />
            <input
              type="date"
              value={dateToInput || ''}
              onChange={(e) => handleDateChange('date_to', e.target.value)}
              onKeyDown={(e) => handleDateKeyDown(e, 'date_to')}
              style={{ padding: '8px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', width: '50%', outline: 'none', background: '#ffffff' }}
            />
          </div>
        </div>

        {/* Business Search */}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Orders Table */}
      <div className={`orders-table-container ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            <h3>No Order History Found</h3>
            <p>No completed, delivered or cancelled orders match the current filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="orders-table-wrapper">
              <table className={`orders-table ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Business</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Order Status</th>
                    <th>Payment Status</th>
                    <th>Delivery Partner</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedOrders.map((order) => (
                    <tr key={`${order.order_system}-${order.order_id}`}>
                      <td className="order-id">
                        <div className="order-number">#{formatOrderId(order.order_id)}</div>
                      </td>
                      <td className="customer-info">
                        <div className="customer-name">{order.customer_name}</div>
                        <div className="customer-phone">{order.customer_phone}</div>
                      </td>
                      <td className="business-name">
                        <div className="business-info">
                          <div className="name">{order.business_name}</div>
                        </div>
                      </td>
                      <td className="order-type">
                        <span className={`type-badge ${order.order_type}`}>{order.order_type}</span>
                      </td>
                      <td className="amount">
                        <div className="amount-info">
                          <div className="total">₹{(order.final_amount ?? order.total_amount ?? 0).toFixed(2)}</div>
                        </div>
                      </td>
                      <td className="status">
                        <span className={`status-badge ${order.status?.toLowerCase()}`}>
                          {getStatusDisplay(order)}
                        </span>
                      </td>
                      <td style={{ minWidth: '100px', padding: '12px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{
                          display: 'inline-block', padding: '4px 8px', borderRadius: '12px',
                          fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
                          backgroundColor: order.payment_status === 'success' ? '#d4edda' :
                            order.payment_status === 'cancelled' ? '#f8d7da' : '#fff3cd',
                          color: order.payment_status === 'success' ? '#155724' :
                            order.payment_status === 'cancelled' ? '#721c24' : '#856404'
                        }}>
                          {(order.payment_status || 'PENDING').toUpperCase()}
                        </div>
                      </td>
                      <td className="delivery-partner">
                        {order.delivery_partner_name ? (
                          <div className="partner-info">
                            <div className="partner-name">{order.delivery_partner_name}</div>
                          </div>
                        ) : (
                          <div className="no-partner">
                            <span className="no-partner-text">N/A</span>
                          </div>
                        )}
                      </td>
                      <td className="created-date">
                        <div className="date-info">
                          <div className="date">{formatDate(order.created_at)}</div>
                        </div>
                      </td>
                      <td className="actions">
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="details-btn"
                            onClick={(e) => { e.stopPropagation(); handleOpenDetailsModal(order); }}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-orders-container">
              {displayedOrders.map((order) => (
                <div key={`mobile-${order.order_system}-${order.order_id}`} className="mobile-order-card">
                  <div className="mobile-order-header">
                    <div className="mobile-order-id">#{formatOrderId(order.order_id)}</div>
                    <span className={`mobile-status-badge ${order.status?.toLowerCase()}`}>
                      {getStatusDisplay(order)}
                    </span>
                  </div>
                  <div className="mobile-order-details">
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaStore /> Business:</div>
                      <div className="mobile-detail-value"><div className="mobile-business-name">{order.business_name}</div></div>
                    </div>
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaUser /> Name:</div>
                      <div className="mobile-detail-value"><div className="mobile-customer-name">{order.customer_name}</div></div>
                    </div>
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaUser /> Phone:</div>
                      <div className="mobile-detail-value"><div className="mobile-customer-phone">{order.customer_phone}</div></div>
                    </div>
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaClipboardList /> Type:</div>
                      <div className="mobile-detail-value">
                        <span className={`mobile-type-badge ${order.order_type}`}>{order.order_type}</span>
                      </div>
                    </div>
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaDollarSign /> Amount:</div>
                      <div className="mobile-detail-value mobile-amount">{formatCurrency(order.final_amount ?? order.total_amount)}</div>
                    </div>
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaCreditCard /> Payment:</div>
                      <div className="mobile-detail-value">
                        <span className={`mobile-payment-badge ${(order.payment_status || 'pending').toLowerCase()}`}>
                          {(order.payment_status || 'PENDING').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {(order.order_type?.toLowerCase().includes('delivery') || order.order_type?.toLowerCase().includes('porter')) && (
                      <div className="mobile-detail-item">
                        <div className="mobile-detail-label"><FaMotorcycle /> Partner:</div>
                        <div className="mobile-detail-value">
                          {order.delivery_partner_name
                            ? <div className="mobile-partner-name">{order.delivery_partner_name}</div>
                            : <span className="mobile-no-partner">N/A</span>
                          }
                        </div>
                      </div>
                    )}
                    <div className="mobile-detail-item">
                      <div className="mobile-detail-label"><FaCalendar /> Date:</div>
                      <div className="mobile-detail-value mobile-created-date">{formatDate(order.created_at)}</div>
                    </div>
                  </div>
                  <div className="mobile-order-actions">
                    <button
                      type="button"
                      className="details-btn"
                      onClick={(e) => { e.stopPropagation(); handleOpenDetailsModal(order); }}
                    >
                      <FaEye /> View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (() => {
          const current = pagination.current_page || 1;
          const total = pagination.total_pages || 1;

          const pages = Array.from({ length: total }, (_, i) => i + 1)
            .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, []);

          const btnBase = {
            width: '44px', height: '44px', borderRadius: '10px',
            border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer',
            fontSize: '15px', fontWeight: '600', color: '#374151',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          };

          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '20px 0 8px' }}>
              <button onClick={() => handlePageChange(current - 1)} disabled={current <= 1}
                style={{ ...btnBase, cursor: current <= 1 ? 'not-allowed' : 'pointer', color: current <= 1 ? '#d1d5db' : '#374151' }}>
                <FaChevronLeft size={13} />
              </button>

              {pages.map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#9ca3af', letterSpacing: '2px' }}>...</span>
                ) : (
                  <button key={item} onClick={() => handlePageChange(item)}
                    style={{
                      ...btnBase,
                      background: current === item ? '#F55D00' : 'white',
                      color: current === item ? 'white' : '#374151',
                      border: current === item ? '1px solid #F55D00' : '1px solid #e5e7eb',
                      boxShadow: current === item ? '0 4px 12px rgba(245, 93, 0, 0.3)' : '0 1px 3px rgba(0,0,0,0.06)'
                    }}>
                    {item}
                  </button>
                )
              )}

              <button onClick={() => handlePageChange(current + 1)} disabled={current >= total}
                style={{ ...btnBase, cursor: current >= total ? 'not-allowed' : 'pointer', color: current >= total ? '#d1d5db' : '#374151' }}>
                <FaChevronRight size={13} />
              </button>
            </div>
          );
        })()}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <OrderHistoryModal
          order={selectedOrder}
          onClose={() => { setShowDetailsModal(false); setSelectedOrder(null); }}
          getStatusDisplay={getStatusDisplay}
          formatOrderId={formatOrderId}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          isManualOrder={isManualOrder}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Separate component — prevents re-mount / flash issues
───────────────────────────────────────────────────────── */
function OrderHistoryModal({ order, onClose, getStatusDisplay, formatOrderId, formatDate, formatDateTime, isManualOrder: isManualOrderProp }) {
  const checkManualOrder = isManualOrderProp || isManualOrder;
  const [showGstBreakdown, setShowGstBreakdown] = useState(false);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const n = (v) => (isNaN(Number(v)) || v == null ? 0 : Number(v));

  const payStyle =
    order.payment_status === 'success'
      ? { background: '#dcfce7', color: '#166534' }
      : order.payment_status === 'failed' || order.payment_status === 'cancelled'
        ? { background: '#fee2e2', color: '#991b1b' }
        : { background: '#fef9c3', color: '#854d0e' };

  const Card = ({ children, style }) => (
    <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', ...style }}>
      {children}
    </div>
  );

  const SectionTitle = ({ icon, title }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
      <span style={{ color: '#F55D00', fontSize: '15px' }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{title}</span>
    </div>
  );

  const Row = ({ label, value, last }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '7px 0', borderBottom: last ? 'none' : '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600, textAlign: 'right', maxWidth: '55%' }}>{value}</span>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 999999,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: '#f1f5f9',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 30px 70px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg,#F55D00 0%,#FDBF50 100%)',
          padding: '18px 24px', color: 'white',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700 }}>ORDER DETAILS</div>
            <div style={{ fontSize: '11px', opacity: 0.85, fontWeight: 600, letterSpacing: '0.5px', marginBottom: '3px' }}>Order #{order.order_id || order.id}</div>

          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className={`status-badge ${(order.status || '').toLowerCase()}`} style={{ fontSize: '10px', padding: '3px 10px', margin: 0 }}>
              {getStatusDisplay(order)}
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.25)', border: 'none', color: 'white',
                width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
                fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginLeft: '4px', flexShrink: 0,
              }}
            >×</button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Card>
              <SectionTitle icon={<FaInfoCircle />} title="Order Information" />
              <Row label="Order Type" value={(order.order_type || 'N/A').toUpperCase()} />
              <Row label="Date" value={formatDate(order.created_at)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Payment Status</span>
                <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', ...payStyle }}>
                  {(order.payment_status || 'PENDING').toUpperCase()}
                </span>
              </div>
              <Row label="Payment Source" value={String(order.payment_source || order.payment_details?.payment_source || order.source || '—').replace(/_/g, ' ').toUpperCase()} />
            </Card>

            <Card>
              <SectionTitle icon={<FaUser />} title="Customer Details" />
              <Row label="Name" value={order.customer?.display_name || order.customer_name || '—'} />
              <Row label="Phone" value={order.customer?.phone || order.customer_phone || '—'} />
              {checkManualOrder(order) && (order.ordered_by_name || order.ordered_by_phone) && (
                <Row label="Ordered By" value={[order.ordered_by_name, order.ordered_by_phone].filter(Boolean).join(' - ')} />
              )}
              {order.order_type?.toLowerCase().includes('porter') && (
                <Row label="Pickup Address" value={
                  (typeof order.pickup_address === 'string' ? order.pickup_address : null) ||
                  order.pickup_address?.full_address || order.pickup_address?.address || '—'
                } />
              )}
            </Card>
          </div>

          {/* Status Timeline (inserted between Row 1 and Row 2) */}
          <Card>
            <SectionTitle icon={<FaInfoCircle />} title="Status Timeline" />
            {Array.isArray(order.status_timeline) && order.status_timeline.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {order.status_timeline.map((st, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < order.status_timeline.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ width: '12px', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B', marginTop: '6px' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{st.label || st.status}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{st.timestamp ? formatDateTime(st.timestamp) : ''}</div>
                      </div>
                      {st.notes ? <div style={{ marginTop: '6px', color: '#334155', fontSize: '13px' }}>{st.notes}</div> : null}
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>By: {st.changed_by || st.source || 'system'}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#64748b' }}>No status timeline available for this order.</div>
            )}
          </Card>

          {/* Row 2 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: (order.order_type?.toLowerCase().includes('delivery') || order.order_type?.toLowerCase().includes('porter')) ? '1fr 1fr' : '1fr',
            gap: '14px'
          }}>
            <Card>
              <SectionTitle icon={<FaBuilding />} title="Business Details" />
              <Row label="Business Name" value={order.business_details?.business_name || order.business?.business_name || order.business_name || '—'} />
              <Row label="Business Phone" value={order.business_details?.business_number || order.business_details?.business_phone || order.business?.business_phone || order.business?.contact_mobile || order.business_phone || '—'} />
              <Row
                label="Address"
                value={
                  [
                    order.business_details?.address || order.business?.address,
                    order.business_details?.landmark || order.business?.landmark,
                    order.business_details?.city || order.business?.city,
                    order.business_details?.state || order.business?.state,
                    order.business_details?.pincode || order.business?.pincode
                  ].filter(Boolean).join(', ') || order.business_address || '—'
                }
                last
              />
            </Card>

            {(order.order_type?.toLowerCase().includes('delivery') || order.order_type?.toLowerCase().includes('porter')) && (
              <Card>
                <SectionTitle icon={<FaTruck />} title="Delivery Partner" />
                <Row label="Partner Name" value={order.delivery_partner_name || 'Not Assigned'} />
                <Row label="Partner Phone" value={order.delivery_partner_phone || '—'} />

                <Row label="Delivery Address" value={
                  order.delivery_address?.full_address ||
                  order.delivery_address?.address ||
                  (typeof order.delivery_address === 'string' ? order.delivery_address : null) ||
                  [order.delivery_address?.street, order.delivery_address?.area, order.delivery_address?.city, order.delivery_address?.state].filter(Boolean).join(', ') ||
                  order.customer?.address || '—'
                } last />
              </Card>
            )}
          </div>

          {/* Order Items + Summary in single row */}
          <div style={{ display: 'grid', gridTemplateColumns: order.items && order.items.length > 0 ? '2fr 1fr' : '1fr', gap: '14px' }}>

            {/* Order Items */}
            {order.items && order.items.length > 0 && (
              <Card>
                <SectionTitle icon={<FaBox />} title={`Order Items (${order.items.length})`} />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {((order.business_type || order.business_details?.business_type || '') !== 'R02'
                        ? ['Item', 'Qty', 'Unit Price', 'Tax', 'Total']
                        : ['Item', 'Qty', 'Unit Price', 'Total']
                      ).map((h, i) => (
                        <th key={h} style={{
                          padding: '8px 10px', textAlign: i === 0 ? 'left' : 'center',
                          fontSize: '11px', fontWeight: 600, color: '#64748b',
                          borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '9px 10px', fontSize: '13px', color: '#1e293b' }}>
                          {item.name || item.item_name || `Item ${idx + 1}`}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                          {item.quantity || 1}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                          ₹{n(item.unit_price ?? item.price).toFixed(2)}
                        </td>
                        {(order.business_type || order.business_details?.business_type || '') !== 'R02' && (
                          <td style={{ padding: '9px 10px', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>
                            ₹{n(item.tax_amount ?? item.gst).toFixed(2)}
                          </td>
                        )}
                        <td style={{ padding: '9px 10px', fontSize: '13px', fontWeight: 600, color: '#1e293b', textAlign: 'center' }}>
                          ₹{n(item.total_price ?? (n(item.unit_price ?? item.price) * n(item.quantity || 1))).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {/* Order Summary */}
            <Card>
              <SectionTitle icon="₹" title="Order Summary" />
              {(() => {
                const summary = order.summary || {};
                const subtotalVal = n(summary.subtotal || order.subtotal || order.total_amount);
                const deliveryVal = n(summary.delivery_charges || order.delivery_charges);
                const parcelVal = n(summary.parcel_charges || order.parcel_charges);
                const discountVal = n(summary.discount || order.discount_amount || order.discount);
                const taxVal = n(summary.tax || order.total_gst_amount || order.gst_amount || order.gst_total);
                const totalVal = n(summary.total || order.final_amount || order.total_amount || (subtotalVal + deliveryVal + parcelVal + taxVal - discountVal));

                const rows = [
                  { id: 'subtotal', label: 'Subtotal', val: `₹${subtotalVal.toFixed(2)}`, show: true },
                  { id: 'delivery', label: 'Delivery Fee', val: `₹${deliveryVal.toFixed(2)}`, show: deliveryVal > 0 || !order.order_type?.toLowerCase().includes('pickup') },
                  { id: 'parcel', label: 'Parcel Charges', val: `₹${parcelVal.toFixed(2)}`, show: parcelVal > 0 },
                  { id: 'tax', label: 'Tax / GST', val: `₹${taxVal.toFixed(2)}`, show: true },
                  { id: 'discount', label: 'Discount', val: `-₹${discountVal.toFixed(2)}`, show: discountVal > 0 },
                ];

                return (
                  <>
                    {rows.filter(r => r.show).map(({ id, label, val }) => (
                      <React.Fragment key={id || label}>
                        {id === 'tax' ? (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowGstBreakdown(!showGstBreakdown)}>
                              <span style={{ color: '#64748b' }}>
                                {label}
                                <span style={{ marginLeft: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                                  {showGstBreakdown ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                </span>
                              </span>
                              <span style={{ fontWeight: 600, color: '#1e293b' }}>{val}</span>
                            </div>
                            {showGstBreakdown && (
                              <div style={{ padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {(() => {
                                  // First check summary tax breakdown
                                  if (summary.tax_breakdown && summary.tax_breakdown.length > 0) {
                                    return summary.tax_breakdown.map((tax, idx) => (
                                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{tax.tax_type} ({tax.tax_percentage}%)</span>
                                        <span>₹{Number(tax.tax_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ));
                                  }

                                  // Else check item tax details (handles array or single object)
                                  const aggregatedTaxes = {};
                                  let hasItemTaxDetails = false;
                                  (order.items || []).forEach(item => {
                                    const rawTaxDetails = item.tax_details;
                                    if (rawTaxDetails) {
                                      const detailsList = Array.isArray(rawTaxDetails) ? rawTaxDetails : [rawTaxDetails];
                                      detailsList.forEach(tax => {
                                        if (tax.cgst_amount || tax.sgst_amount || tax.igst_amount || tax.ugst_amount) {
                                          hasItemTaxDetails = true;
                                          if (tax.cgst_amount) {
                                            const k = 'CGST';
                                            aggregatedTaxes[k] = (aggregatedTaxes[k] || 0) + Number(tax.cgst_amount || 0);
                                          }
                                          if (tax.sgst_amount) {
                                            const k = 'SGST';
                                            aggregatedTaxes[k] = (aggregatedTaxes[k] || 0) + Number(tax.sgst_amount || 0);
                                          }
                                          if (tax.igst_amount) {
                                            const k = 'IGST';
                                            aggregatedTaxes[k] = (aggregatedTaxes[k] || 0) + Number(tax.igst_amount || 0);
                                          }
                                          if (tax.ugst_amount) {
                                            const k = 'UGST';
                                            aggregatedTaxes[k] = (aggregatedTaxes[k] || 0) + Number(tax.ugst_amount || 0);
                                          }
                                        } else if (tax.tax_type) {
                                          hasItemTaxDetails = true;
                                          const key = `${tax.tax_type}_${tax.tax_percentage || ''}`;
                                          if (!aggregatedTaxes[key]) {
                                            aggregatedTaxes[key] = {
                                              label: `${tax.tax_type}${tax.tax_percentage ? ` (${tax.tax_percentage}%)` : ''}`,
                                              amount: 0
                                            };
                                          }
                                          aggregatedTaxes[key].amount += Number(tax.tax_amount || tax.total_gst || 0);
                                        }
                                      });
                                    } else {
                                      const taxAmount = Number(item.gst_amount || item.tax_amount || item.gst || 0);
                                      if (taxAmount > 0) {
                                        hasItemTaxDetails = true;
                                        const half = taxAmount / 2;
                                        aggregatedTaxes['CGST'] = (aggregatedTaxes['CGST'] || 0) + half;
                                        aggregatedTaxes['SGST'] = (aggregatedTaxes['SGST'] || 0) + half;
                                      }
                                    }
                                  });

                                  if (hasItemTaxDetails) {
                                    return Object.entries(aggregatedTaxes).map(([k, valObj], idx) => {
                                      const label = typeof valObj === 'object' ? valObj.label : k;
                                      const amt = typeof valObj === 'object' ? valObj.amount : valObj;
                                      if (amt <= 0) return null;
                                      return (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span>{label}</span>
                                          <span>₹{Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                      );
                                    });
                                  }

                                  if (taxVal > 0) {
                                    const half = taxVal / 2;
                                    return (
                                      <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span>CGST (2.5%)</span>
                                          <span>₹{half.toFixed(2)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span>SGST (2.5%)</span>
                                          <span>₹{half.toFixed(2)}</span>
                                        </div>
                                      </>
                                    );
                                  }

                                  return <div style={{ color: '#94a3b8' }}>No tax breakdown details available.</div>;
                                })()}
                              </div>
                            )}
                          </>
                        ) : (
                          <Row label={label} value={val} />
                        )}
                      </React.Fragment>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0 0', borderTop: '2px solid #e2e8f0', marginTop: '4px' }}>
                      <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: 700 }}>Total</span>
                      <span style={{ fontSize: '17px', color: '#F55D00', fontWeight: 800 }}>
                        ₹{totalVal.toFixed(2)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </Card>

          </div>

        </div>

        {/* ── Modal Footer Actions ── */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e2e8f0',
          background: 'white',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          borderRadius: '0 0 18px 18px',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #F55D00 0%, #FDBF50 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              boxShadow: '0 2px 4px rgba(245, 93, 0, 0.2)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderHistory;
